import { MenuOutlined } from '@ant-design/icons';
import { Button, Card, Drawer, Layout, Typography } from 'antd';
import { useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useCrmNarrowLayout } from './hooks/useCrmNarrowLayout';
import CrmShellMenu from './layout/CrmShellMenu';
import { AuthProvider, useAuth } from './auth/AuthContext';
import LoginPage from './pages/LoginPage';
import AppTermsEditorPage from './pages/modules/AppTermsEditorPage';
import CompanyPriceNoticeEditorPage from './pages/modules/CompanyPriceNoticeEditorPage';
import RideTipsEditorPage from './pages/modules/RideTipsEditorPage';
import ContactUsEditorPage from './pages/modules/ContactUsEditorPage';
import AppShareUrlsPage from './pages/modules/AppShareUrlsPage';
import AppForceUpdatePage from './pages/modules/AppForceUpdatePage';
import DashboardPage from './pages/modules/DashboardPage';
import GenericListPage from './pages/modules/GenericListPage';
import MessagesPage from './pages/modules/MessagesPage';
import PromosPage from './pages/modules/PromosPage';
import AdsPage from './pages/modules/AdsPage';
import DriversPage from './pages/modules/DriversPage';
import DriverLiveLocationPage from './pages/modules/DriverLiveLocationPage';
import PassengersPage from './pages/modules/PassengersPage';
import SettingsNewsPage from './pages/modules/SettingsNewsPage';
import SettingsPassengerPointsPage from './pages/modules/SettingsPassengerPointsPage';
import SettingsPassengerAutoTipPage from './pages/modules/SettingsPassengerAutoTipPage';
import SettingsDriverBusyHoursPage from './pages/modules/SettingsDriverBusyHoursPage';
import SettingsPassengerNearbyDriversPage from './pages/modules/SettingsPassengerNearbyDriversPage';
import SettingsDriverHallPriorityPage from './pages/modules/SettingsDriverHallPriorityPage';
import TopupApprovalPage from './pages/modules/TopupApprovalPage';
import SettingsDriverPointPackagesPage from './pages/modules/SettingsDriverPointPackagesPage';
import DriverAcceptanceRecordsPage from './pages/modules/DriverAcceptanceRecordsPage';
import PassengerOrderRecordsPage from './pages/modules/PassengerOrderRecordsPage';
import RatingsPage from './pages/modules/RatingsPage';
import AuditLogsPage from './pages/modules/AuditLogsPage';
import BookingOptionsPage from './pages/modules/BookingOptionsPage';
import LiveOrdersPage from './pages/modules/LiveOrdersPage';
import OrderChatQuickRepliesPage from './pages/modules/OrderChatQuickRepliesPage';
import AppReleasesPage from './pages/modules/AppReleasesPage';
import SettingsGeminiPage from './pages/modules/SettingsGeminiPage';
import SettingsScheduledOrdersPage from './pages/modules/SettingsScheduledOrdersPage';
import PrivacyPolicyEditorPage from './pages/modules/PrivacyPolicyEditorPage';
import PublicHtmlPage from './pages/public/PublicHtmlPage';
import { crmBrandTitle, crmEnvLabel } from './branding/crmBrand';

const { Header, Sider, Content } = Layout;

function Shell() {
  const { token, admin, logout } = useAuth();
  const narrow = useCrmNarrowLayout();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [siderCollapsed, setSiderCollapsed] = useState(false);
  if (!token) return <Navigate to="/login" replace />;

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <Layout className="crm-shell">
      {!narrow ? (
        <Sider
          theme="light"
          className="crm-sider"
          width={280}
          collapsible
          collapsed={siderCollapsed}
          onCollapse={setSiderCollapsed}
          collapsedWidth={76}
          trigger={null}
        >
          <div className="crm-sider-brand">{crmBrandTitle}</div>
          <CrmShellMenu inlineCollapsed={siderCollapsed} />
        </Sider>
      ) : null}
      <Layout className="crm-main">
        <Header className="crm-header">
          <div className="crm-header-start">
            {narrow ? (
              <Button
                type="text"
                className="crm-menu-trigger"
                icon={<MenuOutlined />}
                aria-label="開啟選單"
                onClick={() => setDrawerOpen(true)}
              />
            ) : (
              <Button
                type="text"
                className="crm-menu-trigger crm-menu-trigger--collapse"
                icon={<MenuOutlined />}
                aria-label={siderCollapsed ? '展開選單' : '收合選單'}
                onClick={() => setSiderCollapsed((c) => !c)}
              />
            )}
            <Typography.Text className="crm-header-user" ellipsis>
              {admin?.displayName ?? '管理員'} · {crmEnvLabel}
            </Typography.Text>
          </div>
          <Button type="link" className="crm-header-logout" onClick={logout}>
            登出
          </Button>
        </Header>
        <Content className="crm-content">
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/live-orders" element={<LiveOrdersPage />} />
            <Route path="/topups" element={<TopupApprovalPage />} />
            <Route path="/driver-point-packages" element={<SettingsDriverPointPackagesPage />} />
            <Route path="/contact" element={<ContactUsEditorPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyEditorPage />} />
            <Route path="/app-terms" element={<AppTermsEditorPage />} />
            <Route path="/company-price-notice" element={<CompanyPriceNoticeEditorPage />} />
            <Route path="/ride-tips" element={<RideTipsEditorPage />} />
            <Route path="/feedbacks" element={<GenericListPage title="意見反饋" endpoint="/admin/feedbacks" />} />
            <Route path="/news" element={<SettingsNewsPage />} />
            <Route path="/app-share-urls" element={<AppShareUrlsPage />} />
            <Route path="/app-force-update" element={<AppForceUpdatePage />} />
            <Route path="/passenger-points" element={<SettingsPassengerPointsPage />} />
            <Route path="/passenger-auto-tip" element={<SettingsPassengerAutoTipPage />} />
            <Route
              path="/passenger-nearby-drivers"
              element={<SettingsPassengerNearbyDriversPage />}
            />
            <Route path="/driver-busy-hours" element={<SettingsDriverBusyHoursPage />} />
            <Route path="/driver-hall-priority" element={<SettingsDriverHallPriorityPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/promos" element={<PromosPage />} />
            <Route path="/ads" element={<AdsPage />} />
            <Route path="/app-releases" element={<AppReleasesPage />} />
            <Route path="/booking-options" element={<BookingOptionsPage />} />
            <Route path="/scheduled-orders" element={<SettingsScheduledOrdersPage />} />
            <Route
              path="/order-chat-quick-replies"
              element={
                <Card>
                  <Typography.Title level={4}>乘客行程快捷訊息</Typography.Title>
                  <OrderChatQuickRepliesPage />
                </Card>
              }
            />
            <Route path="/drivers" element={<DriversPage />} />
            <Route path="/driver-live-location" element={<DriverLiveLocationPage />} />
            <Route path="/passengers" element={<PassengersPage />} />
            <Route path="/users" element={<Navigate to="/drivers" replace />} />
            <Route path="/order-records/drivers" element={<DriverAcceptanceRecordsPage />} />
            <Route path="/order-records/passengers" element={<PassengerOrderRecordsPage />} />
            <Route path="/orders" element={<Navigate to="/order-records/passengers" replace />} />
            <Route path="/ratings" element={<RatingsPage />} />
            <Route path="/audit" element={<AuditLogsPage />} />
            <Route path="/gemini" element={<SettingsGeminiPage />} />
          </Routes>
        </Content>
      </Layout>
      <Drawer
        className="crm-nav-drawer"
        title={crmBrandTitle}
        placement="left"
        width={minDrawerWidth()}
        open={narrow && drawerOpen}
        onClose={closeDrawer}
        styles={{ body: { padding: 0 }, header: { borderBottom: '1px solid rgba(60, 60, 67, 0.12)' } }}
      >
        <CrmShellMenu onNavigate={closeDrawer} />
      </Drawer>
    </Layout>
  );
}

function minDrawerWidth() {
  if (typeof window === 'undefined') return 280;
  return Math.min(320, Math.round(window.innerWidth * 0.86));
}

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/support"
          element={<PublicHtmlPage publicPath="public/contact-us" pageTitle="聯絡我們" />}
        />
        <Route
          path="/privacy"
          element={<PublicHtmlPage publicPath="public/privacy" pageTitle="用戶私隱政策" />}
        />
        <Route path="/user-privacy" element={<Navigate to="/privacy" replace />} />
        <Route path="/*" element={<Shell />} />
      </Routes>
    </AuthProvider>
  );
}
